import { useState } from "react";
import {
  Typography,
  Stack,
  Paper,
  Tooltip,
  Divider,
  alpha,
  Box,
  Skeleton,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  useTheme,
} from "@mui/material";
import { formatAmount } from "../../utils/hooks/dataHandler";
import MainComputationCalculator from "../../builders/calculator/mainComputation";
import CalculateIcon from "@mui/icons-material/Calculate";
import NetProfitSummary from "./NetProfitSummary";
import SummarizeIcon from "@mui/icons-material/Summarize";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { JustInfo } from "../utils/Icon";
import CrossYACalculationSummary from "./CrossYACalculationSummary";

const CalculationSummary = ({
  company,
  corporateIncomeTaxRebate,
  isInvestmentCompany = false,
  isStartup = false,
  netProfitBeforeTax,
  computedSummary,
  loading,
  currency,
  currencyRate,
  masCurrencyRate,
  crossYAApportionmentContext,
  currentYearRebate,
  referenceData = {},
  taxPreviouslyAssessed = [],
  unutilisedCapitalAllowances = [],
  unutilisedDonations = [],
  unutilisedLosses = [],
}) => {
  const theme = useTheme();
  const [showMasRateDetails, setShowMasRateDetails] = useState(false);

  const hasSummaryValue = (value) =>
    value !== null && value !== undefined && value !== "";

  const hasCurrentYearLosses =
    Number(computedSummary.currentYearLosses || 0) > 0;
  const hasOtherSourcedIncome =
    !hasCurrentYearLosses &&
    Array.isArray(referenceData.otherSourceIncome) &&
    referenceData.otherSourceIncome.length > 0;
  const shouldShowCapitalAllowancesResult =
    !hasCurrentYearLosses || hasSummaryValue(computedSummary.unutilisedCAcf);

  const hasCarryForwardSummary =
    (isInvestmentCompany && hasCurrentYearLosses) ||
    hasSummaryValue(computedSummary.unutilisedCAcf) ||
    hasSummaryValue(computedSummary.unutilisedLosscf) ||
    hasSummaryValue(computedSummary.unutilisedDonationcf);
  const localCurrency = String(
    currency ?? company?.currency ?? "SGD"
  ).toUpperCase();
  const showCurrencyLabels = localCurrency !== "SGD";
  const resolvedTaxSummary = {
    ...computedSummary,
    chargeableIncomeBeforeExemptAmount:
      computedSummary.chargebleIncomeBeforeExemptAmount,
    chargeableIncomeAfterExemptAmount:
      computedSummary.chargebleIncomeAfterExemptAmount,
  };
  const currentYearDonationTotal = isInvestmentCompany
    ? MainComputationCalculator.calTaxAdjustments(
        referenceData.currentYearDonations
      )
    : 0;
  const currentYearCaTotal = isInvestmentCompany
    ? MainComputationCalculator.calTaxAdjustments(
        referenceData.currentYearCapitalAllowances
      )
    : 0;
  const currentYearInvestmentLossTotal = isInvestmentCompany
    ? MainComputationCalculator.calTaxAdjustments(
        referenceData.currentYearInvestmentLosses
      )
    : 0;

  const renderDelta = (current, previous, formatAmount) => {
    const delta = current - previous;
    if (previous === null || delta === 0 || hasCurrentYearLosses) {
      return;
    }

    const color = delta >= 0 ? "success.main" : "error.main";
    const sign = delta > 0 ? "+" : "";

    return (
      <Stack direction='row' spacing={0.5} alignItems='center' component='span'>
        <Typography
          component='span'
          sx={{
            color,
            fontSize: "0.875rem",
            fontWeight: 500,
            ml: 1,
          }}
        >
          {sign}
          {previous === null ? "" : formatAmount(delta)}
        </Typography>
      </Stack>
    );
  };

  const formatLabelWithCarryForward = (step, current) => {
    const { carryForwardLabel, isCarryForward, label } = step;

    if (isCarryForward && carryForwardLabel) {
      return carryForwardLabel;
    }

    if (label.includes("Losses") && current < 0) {
      return "Unutilised Losses c/f";
    }
    if (label.includes("Donations") && current < 0) {
      return "Unutilised Donations c/f";
    }
    if (
      label.includes("Adjusted Profit Before Capital Allowances") &&
      current < 0
    ) {
      return "Current Year Losses";
    }
    return label;
  };

  const buildSteps = () => {
    let rawSteps = [];

    if (isInvestmentCompany) {
      // Simplified steps for investment companies
      rawSteps = [
        {
          label: "Net Investment Income",
          value: computedSummary.adjustedProfitAfterTaxAdjustments,
          currencyCode: localCurrency,
        },
        {
          label: "Current Year Capital Allowances",
          value: currentYearCaTotal,
          currencyCode: localCurrency,
          tooltip: "Current year capital allowances tagged from Savy schedules.",
        },
        {
          label: "Current Year Losses",
          value: currentYearInvestmentLossTotal,
          currencyCode: localCurrency,
          tooltip: "Current year investment losses tagged from Savy schedules.",
        },
        {
          label: "Current Year Donations",
          value: currentYearDonationTotal,
          currencyCode: localCurrency,
          tooltip: "Current year donations tagged from Savy schedules.",
        },
        {
          label: "Chargeable Income (Before Exempt)",
          value: resolvedTaxSummary.chargeableIncomeBeforeExemptAmount,
          currencyCode: "SGD",
          tooltip: "Chargeable income before applying any tax exemptions.",
        },
      ];
    } else {
      // Full steps for common companies
      rawSteps = [
        {
          label: "Adjusted Profit Before Capital Allowances",
          value: computedSummary.adjustedProfitAfterTaxAdjustments,
          currencyCode: localCurrency,
          base: netProfitBeforeTax,
          tooltip:
            "Net profit after tax adjustments, before deducting capital allowances.",
        },
        ...(shouldShowCapitalAllowancesResult
          ? [
              {
                label: "Adjusted Profit After Capital Allowances",
                value:
                  !hasOtherSourcedIncome &&
                  hasSummaryValue(computedSummary.unutilisedCAcf)
                    ? computedSummary.unutilisedCAcf
                    : computedSummary.adjustedProfitAfterCapitalAllowances,
                currencyCode: localCurrency,
                carryForwardLabel: "Unutilised Capital Allowances c/f",
                isCarryForward:
                  hasCurrentYearLosses ||
                  (!hasOtherSourcedIncome &&
                    hasSummaryValue(computedSummary.unutilisedCAcf)),
                tooltip:
                  "Profit after deducting capital allowances from the adjusted profit.",
              },
            ]
          : []),
        ...(hasOtherSourcedIncome
          ? [
              {
                label: "Adjusted Profit Before Losses",
                value: hasSummaryValue(computedSummary.unutilisedCAcf)
                  ? computedSummary.unutilisedCAcf
                  : computedSummary.adjustedProfitBeforeLosses,
                currencyCode: localCurrency,
                carryForwardLabel: "Unutilised Capital Allowances c/f",
                isCarryForward: hasSummaryValue(computedSummary.unutilisedCAcf),
                tooltip:
                  "Profit after adding separate source income, before offsetting carried-forward unutilised losses.",
              },
            ]
          : []),
        {
          label: "Adjusted Profit After Unutilised Losses",
          value: hasSummaryValue(computedSummary.unutilisedLosscf)
            ? computedSummary.unutilisedLosscf
            : computedSummary.adjustedProfitAfterLosses,
          currencyCode: localCurrency,
          carryForwardLabel: "Unutilised Losses c/f",
          isCarryForward: hasSummaryValue(computedSummary.unutilisedLosscf),
          tooltip:
            "Remaining profit after offsetting carried-forward unutilised losses.",
        },
        {
          label: "Adjusted Profit After Unutilised Donations",
          value: hasSummaryValue(computedSummary.unutilisedDonationcf)
            ? computedSummary.unutilisedDonationcf
            : computedSummary.adjustedProfitAfterDonations,
          currencyCode: localCurrency,
          carryForwardLabel: "Unutilised Donations c/f",
          isCarryForward: hasSummaryValue(computedSummary.unutilisedDonationcf),
          tooltip:
            "Remaining profit after deducting unutilised approved donations.",
        },
      ];

      if (!hasCarryForwardSummary) {
        rawSteps.push({
          label: "Chargeable Income (Before Exempt)",
          value: resolvedTaxSummary.chargeableIncomeBeforeExemptAmount,
          currencyCode: "SGD",
          tooltip: "Chargeable income before applying any tax exemptions.",
        });
      }
    }

    const steps = [];
    let lastValid = netProfitBeforeTax;

    for (const step of rawSteps) {
      const { currencyCode, label, value, tooltip } = step;
      const isFirstStep = steps.length === 0;

      // Only apply carry-forward formatting for common companies
      const formattedLabel = isInvestmentCompany
        ? label
        : formatLabelWithCarryForward(step, value);

      // Determine whether this is an unutilised item
      const isCarryForward = formattedLabel.includes("c/f");

      // Set compare value accordingly
      const compare =
        isInvestmentCompany ||
        isCarryForward ||
        (showCurrencyLabels && currencyCode === "SGD")
          ? null
          : lastValid >= 0
            ? lastValid
            : 0;

      steps.push({
        label: formattedLabel,
        current: value,
        currencyCode,
        compare,
        tooltip,
      });

      // Update lastValid only if current is positive and not carry-forward
      if (!isCarryForward && value >= 0) {
        lastValid = value;
      }
    }

    return steps;
  };

  const steps = buildSteps();

  return (
    <Paper
      key='Net Profit and Summary'
      id='Net Profit and Summary'
      sx={(theme) => ({
        p: { xs: 3, sm: 4 },
        mb: 4,
        bgcolor: "background.paper",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.08)",
        },
      })}
    >
      <Stack spacing={3}>
        {/* Header Section */}
        <Stack direction='row' alignItems='center' spacing={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <SummarizeIcon color='primary' />
          </Box>
          <Typography
            variant='h5'
            sx={{
              fontWeight: 600,
              color: "text.primary",
              flex: 1,
            }}
          >
            Tax Computation Summary
          </Typography>
          {company.taxPeriodType === "cross_ya" &&
            crossYAApportionmentContext && (
              <Box
                sx={{
                  ml: "auto", // 推到最右边
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.grey[500], 0.08), // 极淡的灰色背景
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ fontWeight: 600, mr: 0.5 }}
                >
                  Days Breakdown
                </Typography>
                <JustInfo
                  title={
                    <>
                      <span>
                        Preceding YA Days:{" "}
                        <strong>
                          {crossYAApportionmentContext?.precedingDays}
                        </strong>
                      </span>
                      <br />
                      <span>
                        Current YA Days:{" "}
                        <strong>
                          {crossYAApportionmentContext?.totalDays -
                            crossYAApportionmentContext?.precedingDays}
                        </strong>
                      </span>
                      <br />
                      <span>
                        Total Days:{" "}
                        <strong>
                          {crossYAApportionmentContext?.totalDays}
                        </strong>
                      </span>
                    </>
                  }
                />
              </Box>
            )}
        </Stack>
        {!isInvestmentCompany && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              border: "1px solid",
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <NetProfitSummary amount={netProfitBeforeTax} />
          </Paper>
        )}
        {/* Exchange Rate Summary Section */}
        {currency !== "SGD" && (currencyRate != null || masCurrencyRate) && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: masCurrencyRate ? "primary.light" : "divider",
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <CurrencyExchangeIcon
                sx={{
                  color: masCurrencyRate ? "primary.main" : "action.active",
                }}
              />
              <Typography variant='subtitle2' fontWeight={600}>
                Exchange Rate ({currency} → SGD)
              </Typography>

              {/* Rate Display */}
              {currencyRate != null ? (
                <Typography variant='body2' color='text.secondary'>
                  Rate: <strong>{currencyRate}</strong>
                  {masCurrencyRate?.rate != null &&
                    currencyRate === masCurrencyRate.rate && (
                      <Chip
                        label='MAS'
                        size='small'
                        color='primary'
                        variant='outlined'
                        sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                      />
                    )}
                  {(masCurrencyRate?.rate == null ||
                    currencyRate !== masCurrencyRate.rate) && (
                    <Chip
                      label='Manual'
                      size='small'
                      variant='outlined'
                      sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                    />
                  )}
                </Typography>
              ) : null}
            </Box>

            {/* MAS Rate Details */}
            {masCurrencyRate?.details?.length > 0 && (
              <>
                <Button
                  size='small'
                  onClick={() => setShowMasRateDetails((p) => !p)}
                  sx={{
                    textTransform: "none",
                    minWidth: "auto",
                    mt: 1,
                    px: 1,
                  }}
                  endIcon={showMasRateDetails ? <ExpandLess /> : <ExpandMore />}
                >
                  {showMasRateDetails ? "Hide" : "View"} monthly breakdown (
                  {masCurrencyRate.details.length} months)
                </Button>

                {showMasRateDetails && (
                  <Paper
                    sx={{
                      mt: 1.5,
                      p: 2,
                      bgcolor: "background.default",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {/* Details Header */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Typography variant='subtitle2' fontWeight={600}>
                        MAS Exchange Rate Details
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {masCurrencyRate.currency ?? currency?.code} → SGD
                      </Typography>
                    </Box>

                    {/* Summary Stats */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2,
                        p: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: 1,
                        opacity: 0.9,
                      }}
                    >
                      <Typography variant='caption' color='text.secondary'>
                        <strong>Average Rate:</strong> {masCurrencyRate.rate}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        <strong>Coverage:</strong>{" "}
                        {masCurrencyRate.monthsIncluded}/
                        {masCurrencyRate.totalMonths} months
                      </Typography>
                      {masCurrencyRate.basisPeriod && (
                        <Typography variant='caption' color='text.secondary'>
                          <strong>Period:</strong>{" "}
                          {new Date(
                            masCurrencyRate.basisPeriod.start
                          ).toLocaleDateString()}{" "}
                          –{" "}
                          {new Date(
                            masCurrencyRate.basisPeriod.end
                          ).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>

                    {/* Rate Table */}
                    <TableContainer sx={{ maxHeight: 200 }}>
                      <Table size='small' stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ py: 1, px: 1 }}>Month</TableCell>
                            <TableCell sx={{ py: 1, px: 1 }}>Type</TableCell>
                            <TableCell sx={{ py: 1, px: 1 }} align='right'>
                              Rate
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {masCurrencyRate.details.map((detail, index) => (
                            <TableRow
                              key={`${detail.month}-${index}`}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                                bgcolor:
                                  index % 2 === 0
                                    ? "transparent"
                                    : "action.hover",
                                "&:hover": {
                                  bgcolor: "action.selected",
                                },
                              }}
                            >
                              <TableCell sx={{ py: 0.5, px: 1 }}>
                                <Typography variant='body2' fontWeight={500}>
                                  {detail.month}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 0.5, px: 1 }}>
                                <Chip
                                  label={
                                    detail.type === "full" ? "Monthly" : "Daily"
                                  }
                                  size='small'
                                  variant='outlined'
                                  color={
                                    detail.type === "full"
                                      ? "success"
                                      : "warning"
                                  }
                                  sx={{ height: 20, fontSize: "0.65rem" }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 0.5, px: 1 }} align='right'>
                                <Typography variant='body2' fontWeight={600}>
                                  {detail.rate}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Footer Note */}
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ mt: 1.5, display: "block", fontStyle: "italic" }}
                    >
                      * Rates sourced from Monetary Authority of Singapore (MAS)
                    </Typography>
                  </Paper>
                )}
              </>
            )}

            {/* Manual Input Notice */}
            {currencyRate != null &&
              (!masCurrencyRate || currencyRate !== masCurrencyRate.rate) && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 1, display: "block", fontStyle: "italic" }}
                >
                  * Manually entered rate.
                  {!masCurrencyRate &&
                    " MAS rates are only available for the previous financial year or your selected currency is not supported."}
                </Typography>
              )}
          </Box>
        )}
        {company.taxPeriodType === "cross_ya" && crossYAApportionmentContext ? (
          <CrossYACalculationSummary
            company={company}
            corporateIncomeTaxRebate={corporateIncomeTaxRebate}
            crossYAApportionmentContext={crossYAApportionmentContext}
            currency={currency}
            currencyRate={currencyRate}
            currentYearRebate={currentYearRebate}
            isInvestmentCompany={isInvestmentCompany}
            isStartup={isStartup}
            loading={loading}
            netProfitBeforeTax={netProfitBeforeTax}
            referenceData={referenceData}
            taxPreviouslyAssessed={taxPreviouslyAssessed}
            unutilisedCapitalAllowances={unutilisedCapitalAllowances}
            unutilisedDonations={unutilisedDonations}
            unutilisedLosses={unutilisedLosses}
          />
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            {/* Header */}
            <Stack
              direction='row'
              alignItems='center'
              spacing={1}
              sx={{ mb: 3 }}
              style={{ cursor: "pointer" }}
            >
              <CalculateIcon color='primary' />
              <Typography variant='h6' fontWeight={600}>
                Computed Summary
              </Typography>
            </Stack>

            <Stack spacing={2}>
              {/* Calculation Steps */}
              {steps.map(
                ({ currencyCode, label, current, compare, tooltip }, index) => (
                  <Paper
                    key={label}
                    variant='outlined'
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: alpha("#603F97", 0.1),
                      bgcolor: alpha("#603F97", 0.02),
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        bgcolor: alpha("#603F97", 0.05),
                        borderColor: alpha("#603F97", 0.2),
                      },
                    }}
                  >
                    <Stack
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <Typography variant='body2' color='text.secondary'>
                          {index + 1}.
                        </Typography>
                        <Tooltip title={tooltip} arrow>
                          <Typography variant='body2' sx={{ cursor: "help" }}>
                            {label}
                          </Typography>
                        </Tooltip>
                      </Stack>
                      <Stack direction='row' spacing={1} alignItems='center'>
                        {loading ? (
                          <Skeleton width={100} height={32} />
                        ) : (
                          <>
                            {showCurrencyLabels && (
                              <Chip
                                label={currencyCode}
                                size='small'
                                color={
                                  currencyCode === "SGD" ? "primary" : "default"
                                }
                                variant='outlined'
                                sx={{ height: 22, fontSize: "0.7rem" }}
                              />
                            )}
                            <Typography
                              variant='subtitle2'
                              fontWeight={600}
                              sx={{
                                color:
                                  current >= 0 ? "success.main" : "error.main",
                              }}
                            >
                              {formatAmount(current)}
                            </Typography>
                            {renderDelta(current, compare, formatAmount)}
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                )
              )}

              {/* Exempt Amount Section */}
              {!hasCarryForwardSummary && (
                <Paper
                  variant='outlined'
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderColor: alpha("#603F97", 0.1),
                    bgcolor:
                      resolvedTaxSummary.chargeableIncomeBeforeExemptAmount !==
                      0
                        ? alpha("#603F97", 0.02)
                        : "background.paper",
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Typography variant='body2' color='text.secondary'>
                        Chargeable Income (After Exempt Amount)
                      </Typography>
                      {showCurrencyLabels && (
                        <Chip
                          label='SGD'
                          size='small'
                          color='primary'
                          variant='outlined'
                          sx={{ height: 20, fontSize: "0.68rem" }}
                        />
                      )}
                    </Stack>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Typography variant='subtitle2' fontWeight={600}>
                        {formatAmount(
                          resolvedTaxSummary.chargeableIncomeAfterExemptAmount
                        )}
                      </Typography>
                      {resolvedTaxSummary.chargeableIncomeBeforeExemptAmount !==
                        0 && (
                        <Typography
                          variant='body2'
                          sx={{
                            color: "error.main",
                            fontSize: "0.875rem",
                          }}
                        >
                          {formatAmount(
                            -(
                              resolvedTaxSummary.firstExemptAmount +
                              resolvedTaxSummary.secondExemptAmount
                            )
                          )}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              )}

              <Divider sx={{ my: 1 }} />

              {/* Tax Calculations */}
              <Paper
                variant='outlined'
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderColor: alpha("#603F97", 0.1),
                  bgcolor: "primary.lighter",
                }}
              >
                <Stack spacing={2}>
                  {showCurrencyLabels && (
                    <Chip
                      label='All amounts in SGD'
                      size='small'
                      color='primary'
                      variant='outlined'
                      sx={{ alignSelf: "flex-start" }}
                    />
                  )}
                  <Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      gutterBottom
                    >
                      Tax Payable at 17%
                    </Typography>
                    <Typography
                      variant='subtitle1'
                      fontWeight={600}
                      color='primary.main'
                    >
                      {formatAmount(resolvedTaxSummary.taxableIncome, false)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      gutterBottom
                    >
                      Tax Payable (Post-Deductions)
                    </Typography>
                    <Typography
                      variant='subtitle1'
                      fontWeight={600}
                      color='primary.main'
                    >
                      {formatAmount(resolvedTaxSummary.postDeductions, false)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      gutterBottom
                    >
                      Net Tax Payable / (Refunded)
                    </Typography>
                    <Typography
                      variant='subtitle1'
                      fontWeight={600}
                      color={
                        resolvedTaxSummary.netTaxPayable > 0
                          ? "error.main"
                          : "success.main"
                      }
                    >
                      {formatAmount(resolvedTaxSummary.netTaxPayable, false)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Paper>
  );
};

export default CalculationSummary;
